import { useEffect, useMemo, useState } from 'react';
import { NavigationHeader } from '@/components/NavigationHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { auth } from '@/firebase';
import { listInstitutions, listCouncilors, listAmendments, type Institution, type Amendment, type Councilor } from '@/services/firestore';
import { getCategoryColors } from '@/lib/category-colors';
import { useToast } from '@/hooks/use-toast';
import { ManagerAmendmentModal } from '@/components/ManagerAmendmentModal';
import { CheckCircle2, XCircle, RotateCcw, AlertCircle, FileText } from 'lucide-react';

export default function GestorArea() {
    const [amendments, setAmendments] = useState<Amendment[]>([]);
    const [institutions, setInstitutions] = useState<Institution[]>([]);
    const [councilors, setCouncilors] = useState<Councilor[]>([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [selectedStatus, setSelectedStatus] = useState<'all' | 'pendente' | 'aprovada' | 'retificar' | 'reprovada'>('all');
    const [detailingFilter, setDetailingFilter] = useState<'all' | 'com-detalhamento' | 'sem-detalhamento'>('all');
    const [selectedAmendment, setSelectedAmendment] = useState<Amendment | null>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const { toast } = useToast();

    const loadData = async () => {
        setLoading(true);
        try {
            const [amendmentsData, institutionsData, councilorsData] = await Promise.all([
                listAmendments(),
                listInstitutions(),
                listCouncilors()
            ]);
            setAmendments(amendmentsData);
            setInstitutions(institutionsData);
            setCouncilors(councilorsData);
        } catch (error) {
            console.error(error);
            toast({ title: 'Erro ao carregar dados', description: 'Tente novamente.', variant: 'destructive' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const institutionById = useMemo(() => {
        return Object.fromEntries(institutions.map(i => [i.id, i]));
    }, [institutions]);

    const councilorById = useMemo(() => {
        return Object.fromEntries(councilors.map(c => [c.id, c]));
    }, [councilors]);

    const filteredAmendments = useMemo(() => {
        const q = search.toLowerCase();
        return amendments.filter(a => {
            const matchSearch = q ? (
                (a.description || '').toLowerCase().includes(q) ||
                (a.officialCode || '').toLowerCase().includes(q) ||
                (institutionById[a.institutionId]?.name || '').toLowerCase().includes(q) ||
                (councilorById[a.councilorId]?.name || '').toLowerCase().includes(q)
            ) : true;

            const matchStatus = selectedStatus === 'all' ? true :
                selectedStatus === 'pendente' ? !a.dispatchStatus :
                    a.dispatchStatus === selectedStatus;

            const hasDetail = !!(a.objectDetail && a.objectDetail.trim().length > 0);
            const matchDetailing = detailingFilter === 'all' ? true :
                detailingFilter === 'com-detalhamento' ? hasDetail :
                    !hasDetail;

            return matchSearch && matchStatus && matchDetailing;
        });
    }, [amendments, search, selectedStatus, detailingFilter, institutionById, councilorById]);

    const stats = useMemo(() => {
        const total = amendments.length;
        const aprovadas = amendments.filter(a => a.dispatchStatus === 'aprovada').length;
        const retificar = amendments.filter(a => a.dispatchStatus === 'retificar').length;
        const reprovadas = amendments.filter(a => a.dispatchStatus === 'reprovada').length;
        const pendentes = total - aprovadas - retificar - reprovadas;

        return { total, aprovadas, retificar, reprovadas, pendentes };
    }, [amendments]);

    const handleOpenModal = (amendment: Amendment) => {
        setSelectedAmendment(amendment);
        setModalOpen(true);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-background font-inter">
                <NavigationHeader userType="gestor" userName={auth.currentUser?.email || undefined} />
                <div className="max-w-7xl mx-auto p-6">
                    <p className="text-muted-foreground">Carregando...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background font-inter">
            <NavigationHeader userType="gestor" userName={auth.currentUser?.email || undefined} />
            <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
                <header className="mb-6">
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-purple-100 rounded-lg">
                            <i className="fas fa-user-tie text-purple-600 text-2xl" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-foreground">Painel do Gestor</h1>
                            <p className="text-muted-foreground">Análise e despacho de emendas parlamentares</p>
                        </div>
                    </div>
                </header>

                {/* Estatísticas */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                    <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setSelectedStatus('all')}>
                        <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground">Total</p>
                                    <p className="text-2xl font-bold">{stats.total}</p>
                                </div>
                                <FileText className="w-8 h-8 text-slate-400" />
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setSelectedStatus('pendente')}>
                        <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground">Pendentes</p>
                                    <p className="text-2xl font-bold text-amber-600">{stats.pendentes}</p>
                                </div>
                                <AlertCircle className="w-8 h-8 text-amber-400" />
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setSelectedStatus('aprovada')}>
                        <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground">Aprovadas</p>
                                    <p className="text-2xl font-bold text-emerald-600">{stats.aprovadas}</p>
                                </div>
                                <CheckCircle2 className="w-8 h-8 text-emerald-400" />
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setSelectedStatus('retificar')}>
                        <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground">Retificar</p>
                                    <p className="text-2xl font-bold text-orange-600">{stats.retificar}</p>
                                </div>
                                <RotateCcw className="w-8 h-8 text-orange-400" />
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setSelectedStatus('reprovada')}>
                        <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground">Reprovadas</p>
                                    <p className="text-2xl font-bold text-red-600">{stats.reprovadas}</p>
                                </div>
                                <XCircle className="w-8 h-8 text-red-400" />
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Filtros e Lista */}
                <Card>
                    <CardHeader>
                        <div className="flex items-center justify-between">
                            <CardTitle>Emendas para Análise</CardTitle>
                            <Badge variant="outline" className="text-sm">
                                {filteredAmendments.length} {filteredAmendments.length === 1 ? 'emenda' : 'emendas'}
                            </Badge>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <div className="flex flex-col sm:flex-row gap-3 mb-4">
                            <Input
                                placeholder="Buscar por descrição, código, instituição ou parlamentar..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="flex-1"
                            />
                            <div className="flex gap-2 flex-wrap">
                                <Button
                                    variant={selectedStatus === 'all' ? 'default' : 'outline'}
                                    size="sm"
                                    onClick={() => setSelectedStatus('all')}
                                >
                                    Todas
                                </Button>
                                <Button
                                    variant={selectedStatus === 'pendente' ? 'default' : 'outline'}
                                    size="sm"
                                    onClick={() => setSelectedStatus('pendente')}
                                >
                                    Pendentes
                                </Button>
                                <Button
                                    variant={selectedStatus === 'aprovada' ? 'default' : 'outline'}
                                    size="sm"
                                    onClick={() => setSelectedStatus('aprovada')}
                                >
                                    Aprovadas
                                </Button>
                                <Button
                                    variant={selectedStatus === 'retificar' ? 'default' : 'outline'}
                                    size="sm"
                                    onClick={() => setSelectedStatus('retificar')}
                                >
                                    Retificar
                                </Button>
                                <Button
                                    variant={selectedStatus === 'reprovada' ? 'default' : 'outline'}
                                    size="sm"
                                    onClick={() => setSelectedStatus('reprovada')}
                                >
                                    Reprovadas
                                </Button>
                                <div className="w-px h-6 bg-border" />
                                <Button
                                    variant={detailingFilter === 'all' ? 'default' : 'outline'}
                                    size="sm"
                                    onClick={() => setDetailingFilter('all')}
                                >
                                    Todas
                                </Button>
                                <Button
                                    variant={detailingFilter === 'com-detalhamento' ? 'default' : 'outline'}
                                    size="sm"
                                    onClick={() => setDetailingFilter('com-detalhamento')}
                                >
                                    Com Detalhamento
                                </Button>
                                <Button
                                    variant={detailingFilter === 'sem-detalhamento' ? 'default' : 'outline'}
                                    size="sm"
                                    onClick={() => setDetailingFilter('sem-detalhamento')}
                                >
                                    Sem Detalhamento
                                </Button>
                            </div>
                        </div>

                        {filteredAmendments.length === 0 ? (
                            <p className="text-muted-foreground text-center py-8">Nenhuma emenda encontrada.</p>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {filteredAmendments.map((amendment) => {
                                    const inst = institutionById[amendment.institutionId];
                                    const councilor = councilorById[amendment.councilorId];
                                    const cat = amendment.category || inst?.category;
                                    const colors = getCategoryColors(cat);

                                    const hasDetail = !!(amendment.objectDetail && amendment.objectDetail.trim().length > 0);

                                    return (
                                        <div
                                            key={amendment.id}
                                            className="border rounded-lg p-4 bg-card hover:shadow-md transition-shadow cursor-pointer"
                                            onClick={() => handleOpenModal(amendment)}
                                        >
                                            <div className="flex items-start justify-between mb-3">
                                                <div className={`p-2 rounded-lg ${colors.bgClass} ${colors.borderClass} border`}>
                                                    <i className={`fas ${colors.icon || 'fa-tag'} ${colors.textClass}`} />
                                                </div>
                                                <div className="flex flex-col gap-1 items-end">
                                                    {amendment.dispatchStatus === 'aprovada' && (
                                                        <Badge className="bg-emerald-600 text-white">Aprovada</Badge>
                                                    )}
                                                    {amendment.dispatchStatus === 'retificar' && (
                                                        <Badge className="bg-orange-500 text-white">Retificar</Badge>
                                                    )}
                                                    {amendment.dispatchStatus === 'reprovada' && (
                                                        <Badge className="bg-red-600 text-white">Reprovada</Badge>
                                                    )}
                                                    {!amendment.dispatchStatus && amendment.status === 'Detalhamento reenviado' && (
                                                        <Badge className="bg-blue-600 text-white">Reenviado</Badge>
                                                    )}
                                                    {!amendment.dispatchStatus && amendment.status !== 'Detalhamento reenviado' && (
                                                        <Badge className="bg-amber-500 text-white">Pendente</Badge>
                                                    )}
                                                    {amendment.detailingSaved ? (
                                                        <Badge variant="outline" className="border-green-500 text-green-700 bg-green-50">
                                                            <i className="fas fa-check-circle mr-1"></i>
                                                            Detalhado
                                                        </Badge>
                                                    ) : hasDetail ? (
                                                        <Badge variant="outline" className="border-blue-500 text-blue-700 bg-blue-50">
                                                            <i className="fas fa-edit mr-1"></i>
                                                            Rascunho
                                                        </Badge>
                                                    ) : (
                                                        <Badge variant="outline" className="border-red-500 text-red-700 bg-red-50">
                                                            <i className="fas fa-exclamation-circle mr-1"></i>
                                                            Sem detalhamento
                                                        </Badge>
                                                    )}
                                                </div>
                                            </div>

                                            <h4 className="font-semibold text-sm mb-1">{inst?.name || 'Instituição'}</h4>
                                            <p className="text-xs text-muted-foreground mb-2 line-clamp-2">{amendment.description}</p>

                                            <div className="mb-2">
                                                <span className="text-xs text-muted-foreground uppercase block">Categoria</span>
                                                <p className="text-xs font-medium">{cat || 'Não definida'}</p>
                                            </div>

                                            <div className="flex justify-between items-center pt-2 border-t">
                                                <div>
                                                    <p className="text-xs text-muted-foreground">Valor</p>
                                                    <p className="font-bold text-sm">
                                                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(amendment.value || 0)}
                                                    </p>
                                                </div>
                                                <div className="text-right">
                                                    <p className="text-xs text-muted-foreground">Parlamentar</p>
                                                    <p className="text-xs font-medium">{councilor?.name || '—'}</p>
                                                </div>
                                            </div>

                                            <p className="text-xs text-muted-foreground mt-2">Emenda: {amendment.officialCode || amendment.id}</p>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {selectedAmendment && (
                <ManagerAmendmentModal
                    open={modalOpen}
                    onOpenChange={setModalOpen}
                    amendment={selectedAmendment}
                    institution={institutionById[selectedAmendment.institutionId] || null}
                    links={[]}
                    onUpdate={() => {
                        loadData();
                        setSelectedAmendment(null);
                    }}
                />
            )}
        </div>
    );
}
